import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { cacheKeys } from "@/lib/cache";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SEOHead } from "@/components/SEOHead";
import { 
  User, 
  MapPin, 
  Lock, 
  Mail, 
  Phone, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  ArrowLeft,
  Home,
  Building,
  Shield
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface UserProfile {
  id: string;
  user_id: string;
  full_name?: string;
  phone?: string;
  bio?: string;
  created_at: string;
  updated_at: string;
}

interface Address {
  id: string;
  user_id: string;
  type: 'shipping' | 'billing';
  is_default: boolean;
  full_name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone?: string;
  created_at: string;
}

const UserProfile = () => {
  const { user, updatePassword, updateEmail, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showAddAddress, setShowAddAddress] = useState(false);

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: cacheKeys.userProfile(user?.id || ''),
    queryFn: async () => {
      if (!isSupabaseConfigured || !user) return null;
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      return data as UserProfile | null;
    },
    enabled: !!user && isSupabaseConfigured,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch addresses
  const { data: addresses, isLoading: addressesLoading } = useQuery({
    queryKey: ['addresses', user?.id],
    queryFn: async () => {
      if (!isSupabaseConfigured || !user) return [];
      const { data, error } = await supabase
        .from('user_addresses')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Address[];
    },
    enabled: !!user && isSupabaseConfigured,
    staleTime: 5 * 60 * 1000,
  });

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: Partial<UserProfile>) => {
      if (!isSupabaseConfigured || !user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKeys.userProfile(user?.id || '') });
      toast.success('Profile updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });

  // Address mutation (create/update)
  const addressMutation = useMutation({
    mutationFn: async (addressData: Partial<Address> & { id?: string }) => {
      if (!isSupabaseConfigured || !user) throw new Error('Not authenticated');

      if (addressData.id) {
        // Update existing address
        const { data, error } = await supabase
          .from('user_addresses')
          .update(addressData)
          .eq('id', addressData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new address
        const { data, error } = await supabase
          .from('user_addresses')
          .insert({
            user_id: user.id,
            ...addressData
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', user?.id] });
      setEditingAddress(null);
      setShowAddAddress(false);
      toast.success('Address saved successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save address');
    },
  });

  // Delete address mutation
  const deleteAddressMutation = useMutation({
    mutationFn: async (addressId: string) => {
      if (!isSupabaseConfigured) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_addresses')
        .delete()
        .eq('id', addressId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', user?.id] });
      toast.success('Address deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete address');
    },
  });

  // Password update mutation
  const updatePasswordMutation = useMutation({
    mutationFn: async (newPassword: string) => {
      const { error } = await updatePassword(newPassword);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Password updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update password');
    },
  });

  // Email update mutation  
  const updateEmailMutation = useMutation({
    mutationFn: async ({ newEmail, password }: { newEmail: string; password: string }) => {
      const { error } = await updateEmail(newEmail, password);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Email update requested! Check your new email for confirmation.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update email');
    },
  });

  if (!user) {
    navigate("/login");
    return null;
  }

  const ProfileForm = () => {
    const [profileData, setProfileData] = useState({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      bio: profile?.bio || ''
    });

    const handleProfileSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      updateProfileMutation.mutate(profileData);
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
          <CardDescription>
            Update your personal details and contact information.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="full-name">Full Name</Label>
              <Input
                id="full-name"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                placeholder="Enter your full name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                rows={4}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user.email || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                To change your email, use the Account Security tab.
              </p>
            </div>

            <Button type="submit" disabled={updateProfileMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  };

  const AddressForm = ({ address, onCancel }: { address?: Address; onCancel: () => void }) => {
    const [formData, setFormData] = useState({
      type: (address?.type || 'shipping') as 'shipping' | 'billing',
      is_default: address?.is_default || false,
      full_name: address?.full_name || '',
      address_line_1: address?.address_line_1 || '',
      address_line_2: address?.address_line_2 || '',
      city: address?.city || '',
      state: address?.state || '',
      postal_code: address?.postal_code || '',
      country: address?.country || 'US',
      phone: address?.phone || '',
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      addressMutation.mutate({
        id: address?.id,
        ...formData
      });
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {address ? 'Edit Address' : 'Add New Address'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Address Type</Label>
                <Select value={formData.type} onValueChange={(value: 'shipping' | 'billing') => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipping">Shipping</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="is-default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                />
                <Label htmlFor="is-default">Make default</Label>
              </div>
            </div>

            <div>
              <Label htmlFor="full-name">Full Name</Label>
              <Input
                id="full-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="address-1">Address Line 1</Label>
              <Input
                id="address-1"
                value={formData.address_line_1}
                onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="address-2">Address Line 2 (Optional)</Label>
              <Input
                id="address-2"
                value={formData.address_line_2}
                onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal-code">Postal Code</Label>
                <Input
                  id="postal-code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="BS">Bahamas</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={addressMutation.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {addressMutation.isPending ? 'Saving...' : 'Save Address'}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  };

  const AddressesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Your Addresses</h3>
          <p className="text-sm text-muted-foreground">Manage your shipping and billing addresses</p>
        </div>
        <Button onClick={() => setShowAddAddress(true)} disabled={showAddAddress || editingAddress}>
          <Plus className="h-4 w-4 mr-2" />
          Add Address
        </Button>
      </div>

      {showAddAddress && (
        <AddressForm onCancel={() => setShowAddAddress(false)} />
      )}

      {editingAddress && (
        <AddressForm address={editingAddress} onCancel={() => setEditingAddress(null)} />
      )}

      {addressesLoading ? (
        <div className="grid gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <LoadingSkeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4">
          {addresses?.map((address) => (
            <Card key={address.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={address.type === 'shipping' ? 'default' : 'secondary'}>
                        {address.type === 'shipping' ? <Home className="h-3 w-3 mr-1" /> : <Building className="h-3 w-3 mr-1" />}
                        {address.type === 'shipping' ? 'Shipping' : 'Billing'}
                      </Badge>
                      {address.is_default && (
                        <Badge variant="outline">Default</Badge>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{address.full_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {address.address_line_1}
                        {address.address_line_2 && `, ${address.address_line_2}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {address.city}, {address.state} {address.postal_code}
                      </p>
                      <p className="text-sm text-muted-foreground">{address.country}</p>
                      {address.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {address.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingAddress(address)}
                      disabled={editingAddress !== null || showAddAddress}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteAddressMutation.mutate(address.id)}
                      disabled={deleteAddressMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {addresses?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No addresses saved yet</p>
              <p className="text-sm">Add your first address to get started</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const SecurityTab = () => {
    const [passwordData, setPasswordData] = useState({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });

    const [emailData, setEmailData] = useState({
      newEmail: '',
      password: ''
    });

    const handlePasswordSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (passwordData.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      updatePasswordMutation.mutate(passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    const handleEmailSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!emailData.newEmail || !emailData.password) {
        toast.error('Please fill in all fields');
        return;
      }
      updateEmailMutation.mutate(emailData);
      setEmailData({ newEmail: '', password: '' });
    };

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" disabled={updatePasswordMutation.isPending}>
                <Shield className="h-4 w-4 mr-2" />
                {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Change Email
            </CardTitle>
            <CardDescription>
              Update your email address. You'll need to verify the new email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <Label htmlFor="current-email">Current Email</Label>
                <Input
                  id="current-email"
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div>
                <Label htmlFor="new-email">New Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={emailData.newEmail}
                  onChange={(e) => setEmailData({ ...emailData, newEmail: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={emailData.password}
                  onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" disabled={updateEmailMutation.isPending}>
                <Mail className="h-4 w-4 mr-2" />
                {updateEmailMutation.isPending ? 'Updating...' : 'Update Email'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Your account details and creation date.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label>User ID</Label>
              <Input
                value={user.id}
                disabled
                className="bg-muted font-mono text-xs"
              />
            </div>
            <div className="grid gap-2">
              <Label>Account Created</Label>
              <Input
                value={new Date(user.created_at).toLocaleDateString()}
                disabled
                className="bg-muted"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between pt-4">
              <div>
                <h4 className="font-medium">Sign out of all devices</h4>
                <p className="text-sm text-muted-foreground">
                  Sign out from all logged-in devices and browsers.
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={async () => {
                  await signOut();
                  navigate("/");
                }}
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <SEOHead
        title="My Profile - Bahama Marketplace"
        description="Manage your Bahama Marketplace account profile, personal information, and preferences."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
                <p className="text-muted-foreground">
                  Manage your personal information and account settings.
                </p>
              </div>
            </div>

            <Tabs defaultValue="profile" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="addresses">Addresses</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="space-y-6">
                {profileLoading ? (
                  <LoadingSkeleton className="h-96 rounded-lg" />
                ) : (
                  <ProfileForm />
                )}
              </TabsContent>

              <TabsContent value="addresses">
                <AddressesTab />
              </TabsContent>

              <TabsContent value="security">
                <SecurityTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;